import React, { PropsWithChildren, useState } from 'react';
import clsx from 'clsx';
import { useSession } from 'next-auth/react';
import { SecondSidebarDesktop, SidebarDesktop } from '../sidebars';
import authUtils from 'utils/auth';
import { headerClientAppOptions, headerLawyerAppOptions } from 'const';
import cmsSidebarNavigation from 'const/sideBarNavigation';
import { NavigationOptions, RoleType } from 'interfaces';
import { Icon, Typography } from 'components/common';
import { CypressI } from 'interfaces/cypress';
import HeaderNavbarDashboard from '../header/headerClientApp';
import { ChatButton } from '@/components/dashboard';
import { useQuery } from '@tanstack/react-query';
import { GET_USER_ME, userService } from '@/api';
import { useRouter } from 'next/router';
import { filterNavigationOptionsByRolesOrPermissions } from '@/utils';
import { customEvent, signOutFunction } from '@/lib';

import Icons from '@/const/icons';
import { handleSetMobileScreenHeader } from '@/lib/handleSetMobileScreenHeader';
import useModal from '@/hooks/useModal';
import MobileSearchModalContent from '@/components/modals/MobileSearchModalContent';
import { useIsSafari } from '@/hooks/useIsSafari';
import Images from '@/const/images';
import { useLayoutFlags } from '@/hooks';
import { PermissionsProvider } from '@/context/PermissionsContext';
import { Permission } from '@/lib/permissions';
import { WelcomeModal } from '@/components/modals/welcome-modal/WelcomeModal';
import { FindCaseModal } from '@/components/modals/welcome-modal/FindCaseModal';
import { GuidedTourModal } from '@/components/modals/guided-tour/GuidedTourModal';
import {
	deleteLocalStorage,
	getLocalStorage,
	setLocalStorage,
} from '@/utils/localStorage';
import { useFirstClientCaseId } from '@/hooks/queries/useFirstClientCaseId';
import { ModalRestriction } from '../../modals/ModalRestriction';
import { LayoutProvider, useLayout } from '@/context/LayoutContext';

export interface LayoutDashboardProps {
	withHeader?: boolean;
	customHeader?: React.ReactNode;

	withSidebar?: boolean;
	customSidebar?: React.ReactNode;

	classNameChildren?: string;
	classNameLayout?: string;

	allowedPermissions?: string[];
	allowedRoles?: string[];
	withButtonChat: boolean;
	pathname: string;
	type: RoleType | undefined;
}

/**
 * This is the main layout component to render a page
 * It has a default behaviour/style but it also considers when a custom component is meant to be rendered
 *
 * @param props they will handle the behaviour of the layout and the styles
 * @returns JSX element for the layout container
 */
export const LayoutDashboard: React.FC<
	PropsWithChildren<LayoutDashboardProps & CypressI>
> = (allProps) => {
	const {
		dataCY,

		withHeader = true,
		customHeader = undefined,

		withSidebar = true,
		customSidebar = undefined,

		children,
		classNameChildren,
		classNameLayout,

		// allowedPermissions,
		// allowedRoles,

		withButtonChat,
		pathname,
		type,
	} = allProps;
	const hasSyncedRef = React.useRef<boolean>(false);
	const session = useSession();
	const logoUrl = Images.hatLogo;

	const router = useRouter();
	const { isSafari } = useIsSafari();
	const { query } = router;
	// SideBar options
	const [sideBarOptions, setSideBarOptions] = useState<NavigationOptions[]>([]);
	const [showSidebar, setShowSidebar] = React.useState<boolean>(false); // Only mobile screen
	const permissions: Permission[] =
		session.data?.user?.user?.lawyer_rol?.permissions ?? [];

	//Mobile screen name
	const [mobileScreenHeader, setMobileScreenHeader] = React.useState<
		{ icon: Icons; screenName: string } | undefined
	>(undefined);

	//mobile search modal
	const { Modal: MobileSearchModal, show, hide } = useModal();

	// Detects if the session has expired
	const {
		data: userMe,
		error,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [GET_USER_ME],
		queryFn: () => userService.getUserMe(),
		// refetchOnWindowFocus: false,
		staleTime: 1000 * 60 * 5,
		gcTime: 1000 * 60 * 30,
		retry: (failureCount, error) => {
			// Do not retry if it is a 401 error
			if ((error as any)?.status === 401) return false;
			// Retry other times according to default settings
			return failureCount < 3;
		},
	});

	// Set session for AuthUtils
	// This is being called in layout to keep session updated
	React.useEffect(() => {
		if (session) authUtils.setSession(session);
	}, [session]);

	// Filter the sidebar options based on the user role
	React.useEffect(() => {
		const filteredOptions = filterNavigationOptionsByRolesOrPermissions(
			cmsSidebarNavigation,
			router.pathname,
			permissions
		);
		setSideBarOptions(filteredOptions);
	}, [session]);

	React.useEffect(() => {
		if (error && (error as any)?.status === 401 && !!session) {
			const role = session.data?.user.user.role || type;
			signOutFunction(role);
		}
	}, [error, session, type]);

	React.useEffect(() => {
		handleSetMobileScreenHeader(router.pathname, setMobileScreenHeader);
	}, [router]);

	React.useEffect(() => {
		if (hasSyncedRef.current) return;
		hasSyncedRef.current = true;

		const stored = getLocalStorage('evaluated');

		if (stored) {
			deleteLocalStorage('evaluated');
			router.replace(
				{
					pathname: router.pathname,
					query: {
						...router.query,
						case_id: stored.case_id,
						evaluated: stored.evaluated,
					},
				},
				undefined,
				{ shallow: true }
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (userMe && query['evaluated']) {
			customEvent({
				action: 'confirm_account_details_click',
				category: 'landing',
				label: 'Confirm Account Details',
				value: 'Confirm Account Details',
			});
		}
	}, [userMe, query]);

	const activeSection = sideBarOptions.find((option) => {
		const isProfilePage =
			option.name === 'client-directory' &&
			pathname.startsWith('/lawyerapp/client-profile');

		return (
			pathname.startsWith(option.href ?? '') ||
			isProfilePage ||
			(option.subOptions &&
				option.subOptions.some((sub) => pathname.startsWith(sub.href ?? '')))
		);
	});

	return (
		<PermissionsProvider permissions={permissions}>
			<LayoutProvider>
				<LayoutDashboardContent
					{...{
						...allProps,
						sideBarOptions,
						permissions,
						userMe,
						showSidebar,
						setShowSidebar,
						mobileScreenHeader,
						show,
						hide,
						isSafari,
						isLoading,
						refetch,
						logoUrl,
						activeSection,
						dataCY,
						withHeader,
						customHeader,
						withSidebar,
						customSidebar,
						children,
						classNameChildren,
						classNameLayout,
						withButtonChat,
					}}
				/>
			</LayoutProvider>
			<ModalRestriction user={userMe?.data} />
			<MobileSearchModal
				avoidClose
				className="!z-[70]"
				classNameMainContainer="!px-0 !items-end !p-0"
				classNameCardContainer="!w-full  !px-4 h-[calc(100vh-37px)] !rounded-b-none rounded-t-[16px] !px-4"
				classNameContainerChildren="!px-0 !overflow-y-hidden"
				noMaxHeight
				transparentBackground
			>
				<MobileSearchModalContent hide={hide} />
			</MobileSearchModal>
		</PermissionsProvider>
	);
};

const LayoutDashboardContent: React.FC<
	PropsWithChildren<
		LayoutDashboardProps &
			CypressI & {
				sideBarOptions: NavigationOptions[];
				permissions: Permission[];
				userMe: any;
				showSidebar: boolean;
				setShowSidebar: (show: boolean) => void;
				mobileScreenHeader: { icon: Icons; screenName: string } | undefined;
				show: () => void;
				hide: () => void;
				isSafari: boolean;
				isLoading: boolean;
				refetch: () => void;
				logoUrl: string;
				activeSection: NavigationOptions | undefined;
			}
	>
> = (props) => {
	const {
		dataCY,
		withHeader = true,
		customHeader = undefined,
		withSidebar = true,
		customSidebar = undefined,
		children,
		classNameChildren,
		classNameLayout,
		withButtonChat,
		pathname,
		type,
		sideBarOptions,
		userMe,
		showSidebar,
		setShowSidebar,
		mobileScreenHeader,
		show,
		isSafari,
		isLoading,
		refetch,
		logoUrl,
		activeSection,
	} = props;

	const router = useRouter();
	const { query } = router;

	const { subSidebar } = useLayout();

	const { isClientMyCases, isLawyerClientDir } = useLayoutFlags(
		pathname,
		isSafari
	);

	const hasSubOptions =
		(subSidebar && !subSidebar.onlyMobile) ||
		(activeSection?.subOptions && activeSection.subOptions.length > 0);

	// const hasMobileSubOptions =
	// 	subSidebar ||
	// 	(activeSection?.subOptions && activeSection.subOptions.length > 0);

	const {
		Modal: ModalAutoGuidedTour,
		show: showAutoGuidedTour,
		hide: hideAutoGuidedTour,
	} = useModal();

	const {
		Modal: ModalFindCase,
		show: showFindCase,
		hide: hideFindCase,
	} = useModal();

	const { totalCases, isLoading: isLoadingCases } = useFirstClientCaseId();

	React.useEffect(() => {
		if (!userMe?.data) return;
		if (userMe.data.role !== 'client') return;
		const guidedTourShownKey = `guided_tour_auto_shown_${userMe.data.id}`;
		if (query['evaluated']) {
			setLocalStorage(guidedTourShownKey, true);
			return;
		}

		if (userMe.data.is_verified) return;
		if (isLoadingCases) return;
		if (totalCases !== 0) return;
		if (getLocalStorage(guidedTourShownKey)) return;

		setLocalStorage(guidedTourShownKey, true);
		setLocalStorage(`guided_tour_seen_${userMe.data.id}`, true);
		showAutoGuidedTour();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		userMe?.data?.id,
		userMe?.data?.role,
		userMe?.data?.is_verified,
		query['evaluated'],
		totalCases,
		isLoadingCases,
	]);

	React.useEffect(() => {
		if (!userMe?.data) return;
		if (userMe.data.role !== 'client') return;
		if (query['evaluated']) return;
		if (!userMe.data.is_verified) return;
		if (isLoadingCases) return;
		if (totalCases !== 0) return;

		const findCaseShownKey = `find_case_modal_shown_${userMe.data.id}`;
		if (getLocalStorage(findCaseShownKey)) return;

		setLocalStorage(findCaseShownKey, true);
		showFindCase();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		userMe?.data?.id,
		userMe?.data?.role,
		userMe?.data?.is_verified,
		query['evaluated'],
		totalCases,
		isLoadingCases,
	]);

	return (
		<div
			data-cy={`${dataCY}-layout`}
			className={clsx('flex w-full h-[99%] md:h-screen', classNameLayout)}
		>
			{query['evaluated'] && userMe && (
				<WelcomeModal user={userMe.data} refetch={refetch} />
			)}

			{!query['evaluated'] && userMe && (
				<>
					<GuidedTourModal
						Modal={ModalAutoGuidedTour}
						onClose={hideAutoGuidedTour}
						onFinish={hideAutoGuidedTour}
					/>
					<FindCaseModal Modal={ModalFindCase} onClose={hideFindCase} />
				</>
			)}

			{/* Sidebar */}
			{withSidebar && !customSidebar && (
				<SidebarDesktop
					role={type}
					dataCY={`${dataCY}-sidebar`}
					itemOptions={sideBarOptions}
					showSidebar={showSidebar}
					logoUrl={logoUrl}
					setShowSidebar={setShowSidebar}
				/>
			)}
			{withSidebar && customSidebar && <>{customSidebar}</>}
			{/* Content of the layout */}
			<div
				className={clsx(
					'flex flex-col h-screen md:min-h-screen w-full overflow-hidden md:overflow-auto bg-transparent',
					{
						'h-[90vh] md:h-screen': isClientMyCases && isSafari,
					},
					classNameChildren
				)}
			>
				{/* Header */}
				{withHeader && !customHeader && (
					<HeaderNavbarDashboard
						dataCY={`${dataCY}-header`}
						logoUrl={logoUrl}
						navBarOptions={
							userMe?.data.role === 'client'
								? headerClientAppOptions
								: headerLawyerAppOptions
						}
						showSidebar={showSidebar}
						setShowSidebar={setShowSidebar}
						firstname={userMe?.data.firstname || ''}
						lastname={userMe?.data.lastname || ''}
						photoUrl={userMe?.data.profile_picture}
						showMobileSearchModal={show}
						isLoading={isLoading}
						role={type}
					/>
				)}
				{withHeader && customHeader && <>{customHeader}</>}
				{/* Mobile screen name indicator */}
				{mobileScreenHeader && (
					<div className="flex items-center gap-2 px-4 pt-6 lg:hidden">
						<Icon
							src={mobileScreenHeader.icon}
							className="!w-5 !h-5 !text-primary"
						/>
						<Typography type="bold-20">
							{mobileScreenHeader.screenName}
						</Typography>
					</div>
				)}
				{/* Right container for actual page content */}
				<div
					id="global-container"
					className={clsx(
						'flex-1 overflow-y-auto scroll-custom pb-14 md:pb-0 -webkit-overflow-scrolling-touch',

						{
							// ⚠️TESTING THIS!!!
							// '!bg-white lg:!bg-gray-15': isInbox,
							// 'dashboard-bg': !isInbox && !isSettings,
							// 'bg-white md:bg-gray-15': isSettings,

							'bg-gray-15': !showSidebar,
							'relative w-full ': withSidebar,
							'md:overflow-y-hidden': isLawyerClientDir,
							'md:pb-28 md:mb-0 h-screen': isSafari,
						}
					)}
				>
					{/* Content - Children */}
					<main
						className={clsx('md:bg-transparent', {
							'relative w-full pb-6 pt-6 px-4 md:px-8 h-auto min-h-full':
								withSidebar,
							'-z-10': showSidebar,
							'lg:pl-56 flex': hasSubOptions,
							// ⚠️TESTING THIS!!!
							// '!p-0 md:!p-8': isInbox,
							// '!py-0': isClientMyCases,
							// 'overflow-y-auto': isSettings,
						})}
					>
						{/* ⚠️TESTING THIS!!! */}
						{/* <div
							className={clsx(
								'flex gap-x-2 justify-between items-center mb-5 md:mb-12',
								{
									'!mb-0': pathname.includes('inbox'),
									hidden: isSettings,
								}
							)}
						/> */}
						{subSidebar ? (
							<SecondSidebarDesktop
								title={subSidebar.title}
								sections={subSidebar.sections}
								onlyMobile={subSidebar.onlyMobile}
							/>
						) : (
							activeSection?.subOptions &&
							activeSection.subOptions.length > 0 && (
								<SecondSidebarDesktop
									title={activeSection.subOptionTitle || activeSection.label}
									sections={activeSection.subOptions}
								/>
							)
						)}
						<div
							className={clsx(
								'relative z-10 flex flex-col w-full'
								// ⚠️TESTING THIS!!!
								// {
								// 	'lg:!h-auto': isLawyerClientProfile || isCustomerSupport,
								// 	'lg:h-[73vh] 2xl:h-[75vh] 3xl:h-[78vh]':
								// 		isClientInbox || isLawyerInbox,
								// 	'lg:h-[73vh] 2xl:h-[80vh]': isLawyerInboxDetail,
								// 	'lg:h-[83vh]': isClientCasesPage,
								// 	'min-h-[89vh] h-auto pb-[100px]':
								// 		isSafari && (isClientDashboard || isClientMyCases),
								// 	'min-h-[83vh] h-auto pb-[50px]':
								// 		isClientDashboard || isClientMyCases,
								// }
							)}
						>
							{children}
						</div>
						{withButtonChat && <ChatButton />}
					</main>
				</div>
			</div>
		</div>
	);
};
